'use client'

import { Countdown } from '@/components/_event/general/countdown'
import ImageWithFallback from '@/components/common/image-with-fallback'
import CalendarIcon2 from '@/components/icons/calendar-icon-2'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { localDateTime } from '@/helpers/datetime'
import { getEventViewButtonState } from '@/helpers/event-results'
import { getFirstNonPracticeSession } from '@/helpers/events'
import dayjs from '@/lib/dayjs'
import { resolveEventImageUrl } from '@/lib/racing-images'
import { cn } from '@/lib/utils'
import apiFetch from '@/store/helpers/api-fetch'
import Link from 'next/link'
import { useMemo, useState } from 'react'
import useSWRImmutable from 'swr/immutable'
import EventCardHydration from './event-card-hydration'

/**
 * This comp requires an event obj with some minmal data or all relevant ID's to fetch the event
 * 
 * It is designed so that it initialy loads with a small amount of data show, and then each card
 * component will fetch for the more (time) expensive data
 * 
 * min Event data required:
    {
      id: true,
      active: true,
      deleteTs: true,
      isPublished: true,
      name: true,
      customTrackImgUrl: true,
      eventRoundNumber: true,
      startDateTime: true,
      finishDateTime: true,
      targetTimezone: true,
      championship: {
        select: {
          id: true,
          name: true,
          slug: true,
          isSingleEvent: true,              
          totalEventCount: true,
          headerImgUrl: true,
          community: {
            select: {
              id: true,
              name: true,
              slug: true,
              logoUrl: true,
              headerImgUrl: true,
            },
          },
        },
      },
    },
*/

export default function EventCard({
  eventFromParent,
  eventId = eventFromParent?.id,
  communityIdOrSlug = eventFromParent?.championship?.community?.slug ||
    eventFromParent?.championship?.community?.id,
  championshipIdOrSlug = eventFromParent?.championship?.slug ||
    eventFromParent?.championship?.id,
  session = null,
  isFinishedEventsSection = false,
  selectedSplits = null,
}) {
  if (
    !eventFromParent & (!eventId || !championshipIdOrSlug || !communityIdOrSlug)
  ) {
    return (
      <div className="col-span-4 flex rounded-sm border border-neutral-blue-700 font-inter">
        <div className="m-auto px-6 text-center text-p-1 font-semibold text-neutral-600">
          Error loading event. No event passed to this component{' '}
        </div>
      </div>
    )
  }

  //if no event object is passed in from parent, fetch it from the api (minimal data)
  const { data: eventFromApi } = useSWRImmutable(
    !eventFromParent
      ? {
          url: `/community/${communityIdOrSlug}/championship/${championshipIdOrSlug}/event/${eventId}`,
          searchParams: {
            select: {
              id: true,
              active: true,
              deleteTs: true,
              isPublished: true,
              name: true,
              customTrackImgUrl: true,
              championship: {
                select: {
                  id: true,
                  name: true,
                  slug: true,
                  isSingleEvent: true,
                  totalEventCount: true,
                  headerImgUrl: true,
                  community: {
                    select: {
                      id: true,
                      name: true,
                      slug: true,
                      logoUrl: true,
                      headerImgUrl: true,
                    },
                  },
                  carClasses: {
                    include: {
                      carClass: true,
                    },
                  },
                },
              },
              eventRoundNumber: true,
              startDateTime: true,
              finishDateTime: true,
              targetTimezone: true,
            },
          },
        }
      : null,
    apiFetch,
  )

  //set event from what is passed in from parent or from an apiFetch
  const event = eventFromParent || eventFromApi

  //All the extra data we need to hydrate the card (the parent comnp should NOT supply this data via the event passed in and it should be fetched here instead)
  //Extra data:
  //  sessions
  //  attendees + gridPositions
  //  track
  const { data: extraEventEventInfo, isLoading: extraEventInfoLoading } =
    useSWRImmutable(
      (eventId && championshipIdOrSlug && communityIdOrSlug) || eventFromParent
        ? {
            url: `/community/${communityIdOrSlug || eventFromParent.championship.community.slug}/championship/${championshipIdOrSlug || eventFromParent.championship.slug}/event/${eventId || eventFromParent.id}`,
            searchParams: {
              select: {
                sessions: {
                  orderBy: {
                    targetDateTime: 'asc',
                  },
                  select: {
                    id: true,
                    resultsPublishedTs: true,
                    type: true,
                    targetDateTime: true,
                    targetTimezone: true,
                    duration: true,
                    durationType: true,
                    splits: {
                      select: {
                        championshipSplit: {
                          select: {
                            id: true,
                            name: true,
                            color: true,
                            priority: true,
                          },
                        },
                      },
                    },
                  },
                },
                attendees: {
                  where: {
                    role: {
                      in: ['driver', 'reserve'],
                    },
                    status: 'registered',
                  },
                  select: {
                    createTs: true,
                    status: true,
                    role: true,
                  },
                },
                track: true,
                trackMeta: true,
                id: true,
                trackId: true,
                trackMeta: true,
              },
            },
          }
        : null,
      apiFetch,
    )

  const [now] = useState(() => new Date())

  const dateTimeCalcs = useMemo(() => {
    const startDateTime =
      !event.startDateTime || !event.targetTimezone
        ? null
        : localDateTime({
            datetime: event.startDateTime,
            timezone: event.targetTimezone,
            format: false,
          })

    const finishDateTime =
      !event.finishDateTime || !event.targetTimezone
        ? null
        : localDateTime({
            datetime: event.finishDateTime,
            timezone: event.targetTimezone,
            format: false,
          })

    const isAfterFinished =
      !event.finishDateTime || !event.targetTimezone
        ? null
        : dayjs(now).isAfter(
            localDateTime({
              datetime: event.finishDateTime,
              format: false,
            }),
          )

    const mainSession = getFirstNonPracticeSession({ event })

    return {
      startDateTime,
      finishDateTime,
      isAfterFinished,
      mainSession,
    }
  }, [event, now])

  const { startDateTime, finishDateTime, isAfterFinished, mainSession } =
    dateTimeCalcs

  return (
    <div className="col-span-4 flex h-full flex-col rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 font-inter">
      <EventHeader
        {...{
          event,
          extraEventEventInfo,
          extraEventInfoLoading,
          session,
          selectedSplits,
        }}
      />

      <div className="flex h-full flex-col px-7 pb-5 pt-6 desktop-xs:pt-8">
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <h5 className="truncate-3 text-h-6 desktop-xs:text-h-5">
                {event.name || 'Unnamed Event'}
              </h5>
            </TooltipTrigger>
            <TooltipContent align="end">
              <p className="text-p-2">{event.name || 'Unnamed Event'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div className="mt-auto flex flex-col pt-6">
          <div className="divide mb-3 bg-neutral-blue-700 desktop-xs:mb-4" />

          <EventStartDate
            {...{
              event,
              startDateTime,
              finishDateTime,
              isAfterFinished,
            }}
          />

          <div className="divide mb-3 mt-3 bg-neutral-blue-700 desktop-xs:mb-4 desktop-xs:mt-4" />

          <EventCardHydration
            {...{
              event,
              extraEventEventInfo,
              extraEventInfoLoading,
              isAfterFinished,
              mainSession,
            }}
          />
        </div>

        <EventViewButton
          {...{
            communityIdOrSlug,
            championshipIdOrSlug,
            event,
            extraEventEventInfo,
            extraEventInfoLoading,
            isAfterFinished,
            session,
          }}
        />

        <EventCountdown
          event={event}
          startDateTime={startDateTime}
          finishDateTime={finishDateTime}
          isFinishedEventsSection={isFinishedEventsSection}
        />
      </div>
    </div>
  )
}

function EventHeader({
  event,
  extraEventEventInfo,
  extraEventInfoLoading,
  selectedSplits,
}) {
  if (!event || extraEventInfoLoading)
    return <Skeleton className="h-[200px] w-full rounded-t-sm" />

  const getUniqueSplits = (extraEventEventInfo) => {
    if (!extraEventEventInfo) return []

    const allSplits = extraEventEventInfo.sessions.flatMap((session) =>
      session.splits.flatMap((s) => s.championshipSplit),
    )

    const uniqueSplits = [...new Set(allSplits.map((split) => split.id))].map(
      (splitId) => allSplits.find((split) => split.id === splitId),
    )

    if (!Array.isArray(selectedSplits)) {
      return uniqueSplits
    }

    return uniqueSplits.filter((split) => selectedSplits.includes(split.id))
  }

  const uniqueSplits = getUniqueSplits(extraEventEventInfo)
  const eventImageUrl = resolveEventImageUrl({
    ...event,
    track: extraEventEventInfo?.track ?? event.track,
  })

  return (
    <div className="group relative h-auto w-full pt-[56%]">
      <ImageWithFallback
        fill
        fallbackUrl="/img/default/header.webp"
        alt="Event Header Image"
        sizes="(min-width: 1280px) calc((100vw - 64px) / 3), (min-width: 768px) calc((100vw - 56px) / 2), calc(100vw - 48px)"
        className="rounded-t-sm object-cover"
        src={eventImageUrl}
      />

      {!event.isSingleEvent && (
        <div className="no-scrollbar absolute bottom-5 left-8 right-8 flex select-none gap-3 overflow-x-auto">
          <p className="text-nowrap rounded-full bg-white/70 px-4 py-2 text-p-1 font-semibold text-black">
            Event {event.eventRoundNumber} /{' '}
            {event.championship.totalEventCount}
          </p>
          {extraEventInfoLoading ? (
            <Skeleton className="h-[35px] w-[95px] rounded-full bg-slate-600/70" />
          ) : (
            uniqueSplits.map((split, index) => (
              <p
                className="h-fit text-nowrap rounded-full bg-medals-gold px-4 py-2 text-p-1 font-semibold"
                style={{
                  backgroundColor: `${
                    split?.color
                      ? split.color
                      : split.priority === 0
                        ? '#FF9900'
                        : split.priority === 1
                          ? '#F2F2F2'
                          : split.priority === 2
                            ? '#FF7222'
                            : 'white'
                  }`,
                }}
                key={index}
              >
                {split.name}
              </p>
            ))
          )}
        </div>
      )}

      <div
        className={cn(
          `z-8 absolute top-0 h-full w-full bg-neutral-blue-900 bg-cover bg-no-repeat opacity-20 transition-all duration-300 group-hover:opacity-0`,
          {
            hidden:
              event.customTrackImgUrl ||
              extraEventEventInfo?.track?.coverImgUrl ||
              event.championship?.headerImgUrl ||
              event.championship?.community?.headerImgUrl,
          },
        )}
      />
    </div>
  )
}

function EventStartDate({
  event,
  startDateTime,
  finishDateTime,
  isAfterFinished,
}) {
  const isStarted = Boolean(startDateTime) && dayjs().isAfter(startDateTime)

  return (
    <>
      {event.finishDateTime ? (
        <>
          <div className="flex w-full items-center gap-2 align-middle">
            <div className="relative flex size-6 min-w-6 items-center rounded-sm bg-neutral-blue-700 desktop-xs:size-[36px] desktop-xs:min-w-[36px]">
              <CalendarIcon2 className="mx-auto -mt-px size-3 stroke-neutral-blue-200 stroke-2 desktop-xs:size-4" />
            </div>
            {!isAfterFinished ? (
              <div className="flex w-full items-center justify-between gap-4 align-middle">
                <p className="text-p-1 font-semibold text-shades-0 desktop-xs:text-p-2">
                  {isStarted ? 'Started' : 'Starts'}
                </p>

                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <p className="truncate-1 break-all text-end text-p-1 text-white desktop-xs:text-p-2">
                        {dayjs(startDateTime).format('ddd D MMM • HH:mm (z)')}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent align="end">
                      <p className="text-p-2">
                        {dayjs(startDateTime).format('ddd D MMM • HH:mm (z)')}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            ) : (
              <div className="flex w-full items-center justify-between gap-4 align-middle">
                <p className="text-p-1 font-semibold text-error-500">
                  Finished
                </p>

                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <p className="truncate-1 break-all text-end text-p-1 text-error-500 desktop-xs:text-p-2">
                        {dayjs(finishDateTime).format('ddd D MMM • HH:mm (z)')}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent align="end">
                      <p className="text-p-2">
                        {dayjs(finishDateTime).format('ddd D MMM • HH:mm (z)')}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex w-full items-center gap-2 align-middle">
          <div className="relative flex size-6 min-w-6 items-center rounded-sm bg-neutral-blue-700 desktop-xs:size-[36px] desktop-xs:min-w-[36px]">
            <CalendarIcon2 className="mx-auto -mt-px size-3 stroke-neutral-blue-200 stroke-2 desktop-xs:size-4" />
          </div>
          <div className="flex w-full items-center justify-between align-middle">
            <p className="text-p-1 font-semibold text-shades-0">Starts</p>
            <p className="text-p-1">N/a </p>
          </div>
        </div>
      )}
    </>
  )
}

function EventViewButton({
  event,
  extraEventEventInfo,
  extraEventInfoLoading,
  isAfterFinished,
}) {
  const eventHref = `/c/${event.championship.community.slug}/${event.championship.slug}/${event.id}`
  const { href, label, showResults } = getEventViewButtonState({
    eventHref,
    isAfterFinished,
    isSingleEvent: event.championship.isSingleEvent,
    sessions: extraEventEventInfo?.sessions,
  })

  return (
    <div className="mt-3 desktop-xs:mt-6">
      {extraEventInfoLoading ? (
        <Skeleton className="h-[62px] w-full" />
      ) : (
        <Link
          href={href}
          className={`hover:border-shades-0! hover:bg-shades-0! group flex h-[60px] items-center rounded-sm border bg-neutral-blue-700 transition-all duration-300 ease-in-out ${
            showResults ? 'border-success-500' : 'border-neutral-blue-600'
          }`}
        >
          <p className="mx-auto text-center font-inter text-p-2 font-semibold text-shades-0 transition-all ease-in-out group-hover:text-black">
            {label}
          </p>
        </Link>
      )}
    </div>
  )
}

/**
 * To display an countdown to the start of the event or show its live
 * @param {date} startDateTime
 * @param {date} finishDateTime
 * @param {boolean} isFinishedEventsSection - for sections where the events are predictably finished (fetched due to finished status)
 * @returns
 */
function EventCountdown({
  event,
  startDateTime,
  finishDateTime,
  isFinishedEventsSection,
}) {
  if (isFinishedEventsSection || !startDateTime || !finishDateTime) {
    return <div className="h-3"></div>
  }

  return (
    <div className="mt-4 flex h-5 w-full">
      <Countdown
        events={[event]}
        targetDate={startDateTime}
        labelText="Starts in "
        countdownClassName={'text-p-1! font-normal! text-neutral-blue-100!'}
        containerClassName={'mx-auto'}
      />
    </div>
  )
}
